import merge from 'lodash/merge';
import { lighten, rgba } from 'polished';
import { debounce } from 'lodash';

export const THEME_WCC = 'wcc';
export const THEME_WCC_RESPONSIVE = 'wcc_responsive';
export const THEME_HOST_BOOKING = 'host_booking';
export const THEME_ATMORE = 'atmore';
export const THEME_WETUMPKA = 'wetumpka';
export const THEME_MONTGOMERY = 'montgomery';
export const THEME_BETHLEHEM = 'bethlehem';
export const THEME_CHICAGO = 'chicago';

export const DEFAULT_THEME = THEME_WCC;

let sliderMaxWidth = window.innerWidth - 55 + 'px';

let checkWidth = debounce(function(e, sliderMaxWidth) {
  sliderMaxWidth =
    window.innerWidth > 0
      ? window.innerWidth - 55 + 'px'
      : window.screen.width - 55 + 'px';
}, 200);

window.addEventListener('resize', checkWidth, true);

const colors = {
  white: '#fff',
  purple: '#ba5cd6',
  darkPurple: '#4a0a9b',
  lightBlue: '#7da3fe',
  blue: '#283278',
  darkBlue: '#122038',
  red: '#ff0000',
  green: '#178757',
  black: '#000',
  grey: '#eee',
  darkGrey: '#c6c6c6',
  lightGrey: '#f2f2f2',
  atmoreRed: '#a32b29',
  atmoreLightRed: '#de3636',
  atmoreDarkRed: '#811b1b',
  atmoreDarkestRed: '#4e0b0b',
  montgomeryLightBlue: '#7da3fe',
  montgomeryBlue: '#283278',
  montgomeryDarkBlue: '#192055',
  montgomeryDarkestBlue: '#0e122e',
  wetumpkaLightOrange: '#ee7522',
  wetumpkaOrange: '#ca4719',
  wetumpkaDarkOrange: '#a03512',
  wetumpkaDarkestOrange: '#782509',
  bethlehemRed: '#a32b29',
  bethlehemLightRed: '#de3636',
  bethlehemDarkRed: '#811b1b',
  bethlehemDarkestRed: '#4e0b0b',
  lightCyanBlue: '#dee2e6',
  disabledInput: '#e9ecef',
  disabledButtonText: '#666'
};

const breakpoints = {
  extraSmall: '400px',
  small: '576px',
  medium: '768px',
  large: '992px',
  extraLarge: '1200px',
  extraExtraLarge: '1499px'
};

const themeDefaults = {
  fullWidth: '100%',
  fontFamily: "'Gotham SSm A', 'Gotham SSm B'",
  fontBold: 'bold',
  textUpperCase: 'uppercase',
  center: '0 auto',
  colors: colors,
  breakpoints: breakpoints,
  buttons: {
    primary: {
      backgroundColor: colors.atmoreRed
    },
    secondary: {
      backgroundColor: colors.atmoreDarkRed
    },
    color: colors.white,
    textUpperCase: 'uppercase',
    border: 0,
    padding: '0.25em 2em',
    block: 'block',
    inline: 'inline',
    cursor: 'pointer'
  },
  calendar: {
    tile: {
      activeBackgroundColor: colors.atmoreLightRed,
      rateFontColor: colors.atmoreDarkRed,
      dayFontColor: colors.atmoreDarkRed,
      disabled: {
        backgroundColor: colors.lightGrey,
        dayFontColor: colors.darkGrey,
        neighboringMonth: {
          backgroundColor: colors.lightGrey,
          dayFontColor: colors.darkGrey
        }
      }
    },
    navigation: {
      backgroundColor: colors.atmoreRed
    },
    minHeight: '550px'
  },
  h5: {
    color: colors.atmoreRed,
    fontSize: '0.8em'
  },
  h4: {
    fontSize: '1.1em'
  },
  arrow: {
    fontSize: '2em'
  },
  a: {
    color: colors.atmoreRed
  },
  table: {
    headerColor: colors.atmoreDarkRed,
    activeRowBackgroundColor: colors.atmoreDarkRed,
    hoverRowBackgroundColor: rgba(255, 255, 255, 0.5)
  },
  forms: {
    input: {
      focusOutline: `0 0 0 0.2rem ${lighten(0.05, colors.atmoreLightRed)}`,
      disabledBackgroundColor: '#e9ecef'
    }
  },
  modal: {
    headerColor: colors.atmoreDarkRed
  },
  roomList: {
    link: {
      color: colors.atmoreDarkRed
    }
  },
  propertySelector: {
    hidden: false,
    hiddenOnMobile: true,
    linkToPropertySite: true
  },
  promotion: {
    backgroundColor: colors.green,
    backgroundHighlightColor: lighten(0.05, colors.green)
  },
  confirmationCarousel: {
    mobileHeight: '25em',
    largeMobileHeight: '20em',
    tabletHeight: '15em',
    desktopHeight: '12em'
  },
  tabs: {
    backgroundColor: colors.atmoreDarkRed,
    fontColor: colors.white,
    indicatorColor: '#ffbf00'
  }
};

export default {
  // we use lodash's merge() func on each theme to override the themeDefaults with theme specific overrides recursively. Using merge instead of assign here to prevent accidental overrides/missing values.
  [THEME_WCC]: merge({}, themeDefaults, {
    buttons: {
      primary: {
        backgroundColor: colors.atmoreRed
      },
      secondary: {
        backgroundColor: colors.atmoreDarkRed
      }
    },
    calendar: {
      tile: {
        activeBackgroundColor: colors.atmoreLightRed,
        rateFontColor: colors.atmoreDarkRed,
        dayFontColor: colors.atmoreDarkRed
      },
      navigation: {
        backgroundColor: colors.atmoreRed
      },
      minHeight: '0px'
    },
    table: {
      headerColor: colors.atmoreDarkRed,
      activeRowBackgroundColor: colors.atmoreDarkRed
    },
    styledSlider: {
      maxWidth: sliderMaxWidth
    },
    scrollableRooms: {
      marginBottom: '0'
    },
    breakpoints: breakpoints,
    propertySelector: {
      linkToPropertySite: false
    },
    confirmationCarousel: {
      mobileHeight: '25em',
      largeMobileHeight: '20em',
      tabletHeight: '15em',
      desktopHeight: '12em'
    }
  }),
  [THEME_WCC_RESPONSIVE]: merge({}, themeDefaults, {
    buttons: {
      primary: {
        backgroundColor: colors.atmoreRed
      },
      secondary: {
        backgroundColor: colors.atmoreDarkRed
      }
    },
    calendar: {
      tile: {
        activeBackgroundColor: colors.atmoreLightRed,
        rateFontColor: colors.atmoreDarkRed,
        dayFontColor: colors.atmoreDarkRed
      },
      navigation: {
        backgroundColor: colors.atmoreRed
      }
    },
    table: {
      headerColor: colors.atmoreDarkRed,
      activeRowBackgroundColor: colors.atmoreDarkRed
    },
    styledSlider: {
      maxWidth: sliderMaxWidth
    },
    scrollableRooms: {
      marginBottom: '0'
    },
    propertySelector: {
      hidden: false,
      hiddenOnMobile: false,
      linkToPropertySite: false
    },
    confirmationCarousel: {
      mobileHeight: '25em',
      largeMobileHeight: '20em',
      tabletHeight: '15em',
      desktopHeight: '12em'
    }
  }),
  [THEME_HOST_BOOKING]: merge({}, themeDefaults, {
    buttons: {
      primary: {
        backgroundColor: colors.atmoreRed
      },
      secondary: {
        backgroundColor: colors.atmoreDarkRed
      }
    },
    calendar: {
      tile: {
        activeBackgroundColor: colors.atmoreLightRed,
        rateFontColor: colors.atmoreDarkRed,
        dayFontColor: colors.atmoreDarkRed
      },
      navigation: {
        backgroundColor: colors.atmoreRed
      }
    },
    table: {
      headerColor: colors.atmoreDarkRed,
      activeRowBackgroundColor: colors.atmoreDarkRed
    },
    styledSlider: {
      maxWidth: sliderMaxWidth
    },
    scrollableRooms: {
      marginBottom: '0'
    },
    propertySelector: {
      hidden: false,
      hiddenOnMobile: false,
      linkToPropertySite: false
    },
    confirmationCarousel: {
      mobileHeight: '25em',
      largeMobileHeight: '20em',
      tabletHeight: '15em',
      desktopHeight: '12em'
    }
  }),
  [THEME_ATMORE]: merge({}, themeDefaults, {
    calendar: {
      tile: {
        activeBackgroundColor: colors.atmoreLightRed,
        rateFontColor: colors.atmoreDarkRed,
        dayFontColor: colors.atmoreDarkRed
      },
      navigation: {
        backgroundColor: colors.atmoreRed
      }
    },
    styledSlider: {
      maxWidth: sliderMaxWidth
    },
    scrollableRooms: {
      marginBottom: '0'
    },
    buttons: {
      primary: {
        backgroundColor: colors.atmoreRed
      },
      secondary: {
        backgroundColor: colors.atmoreDarkRed
      }
    },
    a: {
      color: colors.atmoreRed
    },
    h5: {
      color: colors.atmoreRed,
      fontSize: '0.8em'
    },
    table: {
      headerColor: colors.atmoreDarkRed,
      activeRowBackgroundColor: colors.atmoreDarkRed
    },
    forms: {
      input: {
        focusOutline: `0 0 0 0.2rem ${lighten(0.25, colors.atmoreLightRed)}`
      }
    },
    modal: {
      headerColor: colors.atmoreRed
    },
    roomList: {
      link: {
        color: colors.atmoreRed
      }
    },
    confirmationCarousel: {
      mobileHeight: '20em',
      largeMobileHeight: '20em',
      tabletHeight: '15em',
      desktopHeight: '12em'
    }
  }),
  // TODO: Update for new branding
  [THEME_CHICAGO]: merge({}, themeDefaults, {
    calendar: {
      tile: {
        activeBackgroundColor: colors.atmoreLightRed,
        rateFontColor: colors.atmoreDarkRed,
        dayFontColor: colors.atmoreDarkRed
      },
      navigation: {
        backgroundColor: colors.atmoreRed
      }
    },
    styledSlider: {
      maxWidth: sliderMaxWidth
    },
    scrollableRooms: {
      marginBottom: '0'
    },
    buttons: {
      primary: {
        backgroundColor: colors.atmoreRed
      },
      secondary: {
        backgroundColor: colors.atmoreDarkRed
      }
    },
    a: {
      color: colors.atmoreRed
    },
    h5: {
      color: colors.atmoreRed,
      fontSize: '0.8em'
    },
    table: {
      headerColor: colors.atmoreDarkRed,
      activeRowBackgroundColor: colors.atmoreDarkRed
    },
    forms: {
      input: {
        focusOutline: `0 0 0 0.2rem ${lighten(0.25, colors.atmoreLightRed)}`
      }
    },
    modal: {
      headerColor: colors.atmoreRed
    },
    roomList: {
      link: {
        color: colors.atmoreRed
      }
    },
    confirmationCarousel: {
      mobileHeight: '20em',
      largeMobileHeight: '20em',
      tabletHeight: '15em',
      desktopHeight: '12em'
    }
  }),
  [THEME_MONTGOMERY]: merge({}, themeDefaults, {
    calendar: {
      tile: {
        activeBackgroundColor: colors.montgomeryLightBlue,
        rateFontColor: colors.montgomeryDarkBlue,
        dayFontColor: colors.montgomeryDarkBlue
      },
      navigation: {
        backgroundColor: colors.montgomeryBlue
      }
    },
    styledSlider: {
      maxWidth: sliderMaxWidth
    },
    scrollableRooms: {
      marginBottom: '0'
    },
    buttons: {
      primary: {
        backgroundColor: colors.montgomeryBlue
      },
      secondary: {
        backgroundColor: colors.montgomeryDarkBlue
      }
    },
    a: {
      color: colors.montgomeryBlue
    },
    h5: {
      color: colors.montgomeryBlue,
      fontSize: '0.8em'
    },
    table: {
      headerColor: colors.montgomeryDarkBlue,
      activeRowBackgroundColor: colors.montgomeryDarkBlue
    },
    forms: {
      input: {
        focusOutline: `0 0 0 0.2rem ${lighten(
          0.05,
          colors.montgomeryLightBlue
        )}`
      }
    },
    modal: {
      headerColor: colors.montgomeryBlue
    },
    roomList: {
      link: {
        color: colors.montgomeryBlue
      }
    },
    confirmationCarousel: {
      mobileHeight: '20em',
      largeMobileHeight: '20em',
      tabletHeight: '15em',
      desktopHeight: '13em'
    }
  }),
  [THEME_WETUMPKA]: merge({}, themeDefaults, {
    calendar: {
      tile: {
        activeBackgroundColor: colors.wetumpkaLightOrange,
        rateFontColor: colors.wetumpkaDarkOrange,
        dayFontColor: colors.wetumpkaDarkOrange
      },
      navigation: {
        backgroundColor: colors.wetumpkaOrange
      }
    },
    styledSlider: {
      maxWidth: sliderMaxWidth
    },
    scrollableRooms: {
      marginBottom: '0'
    },
    buttons: {
      primary: {
        backgroundColor: colors.wetumpkaOrange
      },
      secondary: {
        backgroundColor: colors.wetumpkaDarkOrange
      }
    },
    a: {
      color: colors.wetumpkaOrange
    },
    h5: {
      color: colors.wetumpkaOrange,
      fontSize: '0.8em'
    },
    table: {
      headerColor: colors.wetumpkaDarkOrange,
      activeRowBackgroundColor: colors.wetumpkaDarkOrange
    },
    forms: {
      input: {
        focusOutline: `0 0 0 0.2rem ${lighten(
          0.15,
          colors.wetumpkaLightOrange
        )}`
      }
    },
    modal: {
      headerColor: colors.wetumpkaOrange
    },
    roomList: {
      link: {
        color: colors.wetumpkaOrange
      }
    },
    confirmationCarousel: {
      mobileHeight: '20em',
      largeMobileHeight: '20em',
      tabletHeight: '15em',
      desktopHeight: '13em'
    }
  }),
  [THEME_BETHLEHEM]: merge({}, themeDefaults, {
    calendar: {
      tile: {
        activeBackgroundColor: colors.bethlehemLightRed,
        rateFontColor: colors.bethlehemDarkRed,
        dayFontColor: colors.bethlehemDarkRed
      },
      navigation: {
        backgroundColor: colors.bethlehemRed
      }
    },
    styledSlider: {
      maxWidth: sliderMaxWidth
    },
    scrollableRooms: {
      marginBottom: '0'
    },
    buttons: {
      primary: {
        backgroundColor: colors.bethlehemRed
      },
      secondary: {
        backgroundColor: colors.bethlehemDarkRed
      }
    },
    a: {
      color: colors.bethlehemRed
    },
    h5: {
      color: colors.bethlehemRed,
      fontSize: '0.8em'
    },
    table: {
      headerColor: colors.bethlehemDarkRed,
      activeRowBackgroundColor: colors.bethlehemDarkRed
    },
    forms: {
      input: {
        focusOutline: `0 0 0 0.2rem ${lighten(0.25, colors.bethlehemLightRed)}`
      }
    },
    modal: {
      headerColor: colors.bethlehemRed
    },
    roomList: {
      link: {
        color: colors.bethlehemRed
      }
    },
    confirmationCarousel: {
      mobileHeight: '25em',
      largeMobileHeight: '20em',
      tabletHeight: '15em',
      desktopHeight: '12em'
    }
  })
};
